import { useCallback } from "react";
import { useRouter } from "next/router";
import { setCookie } from "nookies";

import { LOCALE_COOKIE, LOCALE_COOKIE_MAX_AGE, Locale } from "./config";
import { useLocale } from "./useTranslations";

/**
 * Switches language and remembers the choice.
 *
 * The cookie is always written explicitly, including for English. Deleting it
 * instead would be a bug: the returning-visitor redirects in next.config.js fire
 * on `NEXT_LOCALE=es`, so a user who switches back to English with no cookie
 * left behind would keep the old one until it expires -- or, having cleared it,
 * would simply be sent back to /es on their next visit by a stale cookie
 * elsewhere. An explicit "en" is the only way to record "I chose English".
 */
export function useLocaleSwitcher() {
  const router = useRouter();
  const locale = useLocale();

  /**
   * Records the choice without navigating, for switchers built out of real
   * links: the anchor handles the navigation, this only has to persist it. The
   * write happens on click, before the browser leaves, so the next request
   * already carries it.
   */
  const rememberLocale = useCallback((next: Locale) => {
    setCookie(null, LOCALE_COOKIE, next, {
      maxAge: LOCALE_COOKIE_MAX_AGE,
      path: "/",
      sameSite: "lax",
    });
  }, []);

  const switchTo = useCallback(
    (next: Locale) => {
      rememberLocale(next);

      // `as: router.asPath` keeps dynamic route params out of the querystring.
      router.push(
        { pathname: router.pathname, query: router.query },
        router.asPath,
        { locale: next },
      );
    },
    [router, rememberLocale],
  );

  return { locale, switchTo, rememberLocale };
}
